/**
 * `<FindReplaceBar>` — the compact Find / Replace panel docked to the
 * top-right of the rich-text editor body (Ctrl/Cmd+F).
 *
 * Not a full-screen modal — Find/Replace reads better as a small floating
 * bar that lets the user keep the document visible while searching. Styled
 * from the app's design tokens (`--bg-1`, `--line-soft`, `--rad`, mono
 * labels) so it feels native; rules live in `richTextEditor.css`
 * (`.find-replace-bar*`).
 *
 * All match-finding / highlighting / replacement logic lives in the
 * `SearchReplaceExtension` ProseMirror plugin — this component is purely the
 * UI: it drives the plugin via `editor.commands.*` and reads the live match
 * state back via `getSearchState(editor.view)`.
 *
 * Behaviour:
 *   - Typing in the find input debounces ~120ms before calling `setSearch`.
 *   - Enter = next match, Shift+Enter = prev match (from the find input).
 *   - Next/Prev buttons mirror that.
 *   - "N of M" counter reflects the plugin's current matches + activeIndex.
 *   - Aa toggle flips case-sensitivity (re-runs the search).
 *   - Replace / Replace all act on the active match / every match.
 *   - Esc closes (handled by the parent, which also clears the search).
 *   - On open, the find input is focused and any current selection text is
 *     seeded in (common find-bar affordance).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';

import { getSearchState } from './searchReplaceExtension';

export interface FindReplaceBarProps {
  editor: Editor;
  /** Called when the bar should close (Esc / close button). The parent is
   *  responsible for clearing the search highlights. */
  onClose: () => void;
}

const DEBOUNCE_MS = 120;

export function FindReplaceBar({
  editor,
  onClose,
}: FindReplaceBarProps): JSX.Element {
  const [searchTerm, setSearchTerm] = useState('');
  const [replaceTerm, setReplaceTerm] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [showReplace, setShowReplace] = useState(false);
  // Mirror of the plugin's match state for the counter. Bumped via a
  // re-render trigger after every command (the plugin owns the source of
  // truth; we just read it back).
  const [matchInfo, setMatchInfo] = useState<{ count: number; active: number }>(
    { count: 0, active: 0 },
  );

  const findInputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<number | null>(null);

  // Pull the latest match count + active index out of the plugin.
  const syncMatchInfo = useCallback(() => {
    const s = getSearchState(editor.view);
    setMatchInfo({
      count: s.matches.length,
      active: s.matches.length === 0 ? 0 : s.activeIndex + 1,
    });
  }, [editor]);

  // Push a search term into the plugin (debounced from the input handler) and
  // refresh the counter.
  const runSearch = useCallback(
    (term: string, cs: boolean) => {
      editor.commands.setSearch(term, cs);
      syncMatchInfo();
    },
    [editor, syncMatchInfo],
  );

  // On mount: focus the find input + seed it from the current selection so
  // "select a word, Cmd+F" pre-fills the term (standard find-bar UX).
  useEffect(() => {
    const { from, to } = editor.state.selection;
    let seed = '';
    if (to > from) {
      seed = editor.state.doc.textBetween(from, to, ' ').trim();
    }
    if (seed.length > 0) {
      setSearchTerm(seed);
      runSearch(seed, caseSensitive);
    }
    findInputRef.current?.focus();
    findInputRef.current?.select();
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clean up any pending debounce timer on unmount.
  useEffect(() => {
    return () => {
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const onFindChange = (value: string): void => {
    setSearchTerm(value);
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      runSearch(value, caseSensitive);
    }, DEBOUNCE_MS);
  };

  const onToggleCase = (): void => {
    const next = !caseSensitive;
    setCaseSensitive(next);
    // Re-run immediately with the new flag (no debounce — it's a click).
    runSearch(searchTerm, next);
  };

  const goNext = (): void => {
    editor.commands.nextMatch();
    syncMatchInfo();
  };

  const goPrev = (): void => {
    editor.commands.prevMatch();
    syncMatchInfo();
  };

  const onFindKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (e.shiftKey) {
        goPrev();
      } else {
        goNext();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  const onReplaceKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      doReplace();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  const doReplace = (): void => {
    editor.commands.replaceCurrent(replaceTerm);
    // The plugin recomputes matches off the doc-change transaction; read the
    // refreshed state back for the counter.
    syncMatchInfo();
  };

  const doReplaceAll = (): void => {
    editor.commands.replaceAll(replaceTerm);
    syncMatchInfo();
  };

  const hasMatches = matchInfo.count > 0;
  const counterText =
    searchTerm.length === 0
      ? ''
      : hasMatches
        ? `${matchInfo.active} of ${matchInfo.count}`
        : 'No results';

  return (
    <div
      className="find-replace-bar"
      role="search"
      aria-label="Find and replace"
    >
      <div className="find-replace-bar__row">
        <input
          ref={findInputRef}
          type="text"
          className="find-replace-bar__input"
          placeholder="Find"
          value={searchTerm}
          onChange={(e) => onFindChange(e.target.value)}
          onKeyDown={onFindKeyDown}
          aria-label="Find"
        />
        <span
          className="find-replace-bar__count"
          aria-live="polite"
        >
          {counterText}
        </span>
        <button
          type="button"
          className={
            caseSensitive
              ? 'find-replace-bar__btn find-replace-bar__btn--active'
              : 'find-replace-bar__btn'
          }
          onClick={onToggleCase}
          title="Match case"
          aria-pressed={caseSensitive}
        >
          Aa
        </button>
        <button
          type="button"
          className="find-replace-bar__btn"
          onClick={goPrev}
          disabled={!hasMatches}
          title="Previous match (Shift+Enter)"
          aria-label="Previous match"
        >
          ↑
        </button>
        <button
          type="button"
          className="find-replace-bar__btn"
          onClick={goNext}
          disabled={!hasMatches}
          title="Next match (Enter)"
          aria-label="Next match"
        >
          ↓
        </button>
        <button
          type="button"
          className={
            showReplace
              ? 'find-replace-bar__btn find-replace-bar__btn--active'
              : 'find-replace-bar__btn'
          }
          onClick={() => setShowReplace((v) => !v)}
          title="Toggle replace"
          aria-pressed={showReplace}
          aria-label="Toggle replace"
        >
          ⇄
        </button>
        <button
          type="button"
          className="find-replace-bar__btn"
          onClick={onClose}
          title="Close (Esc)"
          aria-label="Close find"
        >
          ✕
        </button>
      </div>

      {showReplace && (
        <div className="find-replace-bar__row">
          <input
            type="text"
            className="find-replace-bar__input"
            placeholder="Replace"
            value={replaceTerm}
            onChange={(e) => setReplaceTerm(e.target.value)}
            onKeyDown={onReplaceKeyDown}
            aria-label="Replace with"
          />
          <button
            type="button"
            className="find-replace-bar__action"
            onClick={doReplace}
            disabled={!hasMatches}
          >
            Replace
          </button>
          <button
            type="button"
            className="find-replace-bar__action"
            onClick={doReplaceAll}
            disabled={!hasMatches}
          >
            Replace all
          </button>
        </div>
      )}
    </div>
  );
}
